import { useState } from "react";

import {
  errorMessage,
  useConsoleKeys,
  useCreateConsoleKey,
  useRevokeConsoleKey,
} from "../../api/queries";
import type { ConsoleKey, ConsoleKeyExpiry } from "../../api/types";
import { formatDateTime } from "../../lib/format";
import { CopyBlock } from "../ui/CopyBlock";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { ErrorBlock, LoadingBlock } from "../ui/StateBlock";

/**
 * The three lifetimes the API accepts (`docs/API.md`).
 *
 * No expiry is the default and it is deliberate: a key that dies on a Tuesday
 * breaks a run started on Monday, and the fix a researcher learns is to pick
 * the longest option every time. Revoking here, and the last use shown beside
 * every key, are the control instead.
 */
const EXPIRY_CHOICES: readonly { value: ConsoleKeyExpiry; label: string }[] = [
  { value: null, label: "No expiry" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

/**
 * What this key has been doing, in one line.
 *
 * Args:
 *     item: The key as the server lists it.
 *
 * Returns:
 *     Its prefix, its age, its last use and its expiry, in plain words.
 */
function describeKey(item: ConsoleKey): string {
  const used = item.last_used_at
    ? `last used ${formatDateTime(item.last_used_at)}`
    : "never used yet";
  const expiry = item.expires_at
    ? `stops working ${formatDateTime(item.expires_at)}`
    : "no expiry";
  return `${item.prefix} · created ${formatDateTime(item.created_at)} · ${used} · ${expiry}`;
}

interface KeyRowProps {
  item: ConsoleKey;
  onRevoke: (item: ConsoleKey) => void;
  /** True while any revocation is in flight. */
  busy: boolean;
}

/** One key on file: what it is called, what it is, and one way to kill it. */
function KeyRow({ item, onRevoke, busy }: KeyRowProps): JSX.Element {
  return (
    <li className="console-keys__row">
      <div className="console-keys__identity">
        <span className="console-keys__name">{item.name}</span>
        {/* The prefix, and nothing else: it is not a shortened secret and
            cannot be turned into one, which is why it may be shown at all. */}
        <span className="console-keys__meta mono">{describeKey(item)}</span>
      </div>
      <button
        type="button"
        className="btn btn--sm"
        disabled={busy}
        // Two keys may share a name, so the prefix is what tells them apart —
        // here as well as in the confirmation.
        aria-label={`Revoke the key ${item.name} (${item.prefix})`}
        onClick={() => onRevoke(item)}
      >
        Revoke
      </button>
    </li>
  );
}

/**
 * The console keys this account's terminals carry.
 *
 * A second credential beside the SSH keys above, and for a different door: an
 * SSH key opens a machine, a console key drives the console itself from a
 * shell (`docs/AUTH.md` §7). The browser is the only place one can be made,
 * which is what keeps it derived from a Google sign-in rather than a way
 * around one.
 *
 * Three things happen here and nothing else: a key is created and **shown
 * once**, the keys that can reach the account are listed without their
 * secrets, and any of them is revoked. The secret exists in the creation
 * response and nowhere else — this component never stores it, and drops it the
 * moment the panel showing it is dismissed or left.
 *
 * Returns:
 *     The keys on file, the form that mints one, and the revoke confirmation.
 */
export function ConsoleKeySection(): JSX.Element {
  const keys = useConsoleKeys();
  const create = useCreateConsoleKey();
  const revoke = useRevokeConsoleKey();

  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<ConsoleKeyExpiry>(null);
  /** The key a confirmation is open for; null when none is. */
  const [revoking, setRevoking] = useState<ConsoleKey | null>(null);

  const created = create.data ?? null;

  const submit = (): void => {
    create.mutate(
      { name: name.trim(), expires_days: expiry },
      {
        onSuccess: () => {
          setName("");
          setExpiry(null);
        },
      },
    );
  };

  const closeRevoke = (): void => {
    setRevoking(null);
    revoke.reset();
  };

  if (keys.isPending) {
    return <LoadingBlock label="Reading your console keys…" />;
  }
  if (keys.isError) {
    return <ErrorBlock error={keys.error} onRetry={() => void keys.refetch()} />;
  }

  const items = keys.data.items;

  return (
    <div className="console-keys-block">
      {created ? (
        <div className="console-key-secret">
          <p className="console-key-secret__title" role="status">
            Copy it now — this is the only time it is shown.
          </p>
          <CopyBlock
            value={created.key}
            label="Copy your new console key"
            multiline
          />
          <p className="form__note">
            The console stores a hash of this key, never the key itself: it
            cannot show it to you again, and neither can an admin. If you lose
            it, revoke it here and create another — that costs two clicks.
          </p>
          <p className="form__note">
            On the machine it is for, run{" "}
            <span className="mono">nk login</span> and paste it when asked.
            It is not echoed there, so it stays out of that shell&apos;s
            history.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              create.reset();
            }}
          >
            Done, I have copied it
          </button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <>
          <p className="form__note">
            A console key lets the <span className="mono">nk</span> command line
            act as you from a terminal — submit a run, watch it, stop it —
            without leaving the shell your code is in. Install the command line
            first, then create a key below and paste it into{" "}
            <span className="mono">nk login</span> on that machine.
          </p>
          <CopyBlock
            value='uv tool install "git+ssh://git@github.com/alexiswychovaniak/gpu-console.git#subdirectory=cli"'
            label="Copy the command that installs the command line"
          />
        </>
      ) : (
        <ul className="console-keys">
          {items.map((item) => (
            <KeyRow
              key={item.id}
              item={item}
              onRevoke={setRevoking}
              busy={revoke.isPending}
            />
          ))}
        </ul>
      )}

      <form
        className="console-key-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field
          label="Name this key"
          htmlFor="console-key-name"
          hint="The machine it will live on — your laptop, a login node. One key per machine means a machine you lose is a line you revoke."
        >
          <input
            id="console-key-name"
            className="input"
            type="text"
            value={name}
            autoComplete="off"
            spellCheck={false}
            placeholder="laptop"
            aria-describedby="console-key-name-hint"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <div className="field">
          <span className="field__label">How long it lives</span>
          <fieldset className="choice-row" disabled={create.isPending}>
            <legend className="sr-only">How long it lives</legend>
            {EXPIRY_CHOICES.map((choice) => (
              <label
                key={String(choice.value)}
                className="choice"
                data-selected={expiry === choice.value}
              >
                <input
                  type="radio"
                  name="console-key-expiry"
                  className="sr-only"
                  value={String(choice.value)}
                  checked={expiry === choice.value}
                  onChange={() => setExpiry(choice.value)}
                />
                {choice.label}
              </label>
            ))}
          </fieldset>
          <span className="field__hint">
            No expiry is the default. Revoking here, and the last use shown
            beside every key, are what keep this list honest — a clock only
            breaks work somebody started yesterday.
          </span>
        </div>

        {create.isError ? (
          <p className="form__error" role="alert">
            {errorMessage(create.error)}
          </p>
        ) : null}

        <button
          type="submit"
          className="btn btn--primary"
          disabled={create.isPending || !name.trim()}
        >
          {create.isPending ? "Creating…" : "Create a key"}
        </button>
      </form>

      {revoking ? (
        <Dialog
          title="Revoke this console key"
          onClose={closeRevoke}
          footer={
            <>
              <button type="button" className="btn" onClick={closeRevoke}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={revoke.isPending}
                onClick={() =>
                  revoke.mutate(revoking.id, { onSuccess: closeRevoke })
                }
              >
                {revoke.isPending ? "Revoking…" : "Revoke it"}
              </button>
            </>
          }
        >
          <p className="dialog__text">
            <span className="mono">
              {revoking.name} ({revoking.prefix})
            </span>{" "}
            stops working at its next request. Work it already started keeps
            running, and nothing else on your account is touched.
          </p>
          <p className="dialog__text">
            A terminal still holding it will be refused from that moment —
            create a new key there when you need one.
          </p>
          {revoke.isError ? (
            <p className="dialog__error" role="alert">
              {errorMessage(revoke.error)}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </div>
  );
}
